/**
 * ChecklistItemDialog - Criação/edição de item do Template de Checklist
 *
 * Campos: nome, descrição, tipo, obrigatório, seção, texto de ajuda, valor
 * padrão, exige aprovação, ativo e opções específicas por tipo.
 * Tipo de campo fica bloqueado quando o item já foi publicado (regra do banco).
 *
 * @version 1.3.0
 * RELEASE: 2026-09-28 v1.3.0 - Configuração de foto modelo privada para itens visuais.
 * RELEASE: 2026-09-26 v1.2.0 - Criação e edição alinhadas ao modal premium de anotações do chat.
 * RELEASE: 2026-09-26 v1.1.0 - Botões textuais alinhados ao padrão compacto de Departamentos.
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial (Fase 1b)
 */
import { useEffect, useRef, useState } from 'react';
import { ClipboardList, ImagePlus, Loader2, Lock, Plus, Save, Settings2, ShieldCheck, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CHECKLIST_FIELD_TYPES, signedChecklistReferencePhoto, uploadChecklistReferencePhoto,
  type ChecklistFieldType, type ChecklistItem, type ChecklistReferencePhoto, type ChecklistSection,
} from '@/hooks/useChecklistTemplates';
import { useAuth } from '@/hooks/useAuth';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: ChecklistItem | null;
  defaultSectionId: string | null;
  sections: ChecklistSection[];
  templateId: string;
  saving: boolean;
  onSave: (data: Record<string, unknown>) => Promise<unknown>;
}

const NO_SECTION = '__none__';
const hasHtml = (s: string) => /<[^>]+>/.test(s);

export function ChecklistItemDialog({ open, onOpenChange, item, defaultSectionId, sections, templateId, saving, onSave }: Props) {
  const { currentTenant } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fieldType, setFieldType] = useState<ChecklistFieldType>('yes_no');
  const [isRequired, setIsRequired] = useState(false);
  const [sectionId, setSectionId] = useState<string>(NO_SECTION);
  const [placeholder, setPlaceholder] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [maxLength, setMaxLength] = useState('');
  const [maxFiles, setMaxFiles] = useState('1');
  const [cameraOnly, setCameraOnly] = useState(false);
  const [referencePhoto, setReferencePhoto] = useState<ChecklistReferencePhoto | null>(null);
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [referencePreview, setReferencePreview] = useState<string | null>(null);
  const [uploadingReference, setUploadingReference] = useState(false);
  const referenceInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // Carrega valores ao abrir
  useEffect(() => {
    if (!open) return;
    const c = (item?.config ?? {}) as Record<string, unknown>;
    setName(item?.name ?? '');
    setDescription(item?.description ?? '');
    setFieldType(item?.field_type ?? 'yes_no');
    setIsRequired(item?.is_required ?? false);
    setSectionId(item ? item.section_id ?? NO_SECTION : defaultSectionId ?? NO_SECTION);
    setPlaceholder(item?.placeholder ?? '');
    setDefaultValue(item?.default_value == null ? '' : String(item.default_value));
    setRequiresApproval(item?.requires_approval ?? true);
    setIsActive(item?.is_active ?? true);
    setOptions(Array.isArray(c.options) ? (c.options as string[]) : []);
    setMin(c.min != null ? String(c.min) : '');
    setMax(c.max != null ? String(c.max) : '');
    setMaxLength(c.max_length != null ? String(c.max_length) : '');
    setMaxFiles(c.max_files != null ? String(c.max_files) : '1');
    setCameraOnly(Boolean(c.camera_only));
    const storedReference = c.reference_photo && typeof c.reference_photo === 'object'
      ? c.reference_photo as ChecklistReferencePhoto : null;
    setReferencePhoto(storedReference);
    setReferenceFile(null);
    setReferencePreview(null);
    if (storedReference?.storage_path) {
      signedChecklistReferencePhoto(storedReference.storage_path).then(setReferencePreview).catch(() => setReferencePreview(null));
    }
    setNewOption('');
    setError(null);
  }, [open, item, defaultSectionId]);

  const isSelect = fieldType === 'single_select' || fieldType === 'multi_select';
  const isFile = fieldType === 'file' || fieldType === 'photo' || fieldType === 'photo_or_file';
  const supportsReference = fieldType === 'photo' || fieldType === 'photo_or_file';
  const typeLocked = !!item?.ever_published;

  const addOption = () => {
    const v = newOption.trim();
    if (!v || v.length > 100 || hasHtml(v) || options.includes(v) || options.length >= 50) return;
    setOptions([...options, v]);
    setNewOption('');
  };

  /** Valida e monta o payload enviado à RPC */
  const handleSave = async () => {
    const n = name.trim();
    if (!n) return setError('Informe o nome do campo');
    if ([n, description, placeholder, defaultValue].some(hasHtml)) return setError('HTML não é permitido');
    if (isSelect && options.length === 0) return setError('Adicione ao menos uma opção');

    const config: Record<string, unknown> = {};
    if (isSelect) config.options = options;
    if (fieldType === 'number') {
      if (min) config.min = Number(min);
      if (max) config.max = Number(max);
    }
    if (fieldType === 'text' && maxLength) config.max_length = Math.min(Number(maxLength), 5000);
    if (isFile) {
      config.max_files = Math.max(1, Math.min(Number(maxFiles) || 1, 20));
      if (fieldType === 'photo') config.camera_only = cameraOnly;
    }

    let savedReference = supportsReference ? referencePhoto : null;
    if (supportsReference && referenceFile) {
      if (!currentTenant?.id) return setError('Tenant não identificado');
      setUploadingReference(true);
      try {
        savedReference = await uploadChecklistReferencePhoto(currentTenant.id, templateId, referenceFile);
      } catch (e) {
        setUploadingReference(false);
        return setError((e as Error).message || 'Não foi possível enviar a foto modelo');
      }
      setUploadingReference(false);
    }
    if (savedReference) config.reference_photo = savedReference;

    await onSave({
      name: n,
      description: description.trim() || null,
      field_type: fieldType,
      is_required: isRequired,
      section_id: sectionId === NO_SECTION ? null : sectionId,
      placeholder: placeholder.trim() || null,
      default_value: isFile || !defaultValue.trim() ? null : defaultValue.trim(),
      requires_approval: requiresApproval,
      is_active: isActive,
      config,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90dvh] max-h-[90dvh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:h-auto sm:min-h-[36rem]">
        {/* Cabeçalho premium compartilhado com os modais de anotações do chat. */}
        <div className="shrink-0 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent px-6 py-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <ClipboardList className="h-3.5 w-3.5 text-primary" />
              </span>
              {item ? 'Editar item' : 'Novo item'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Configure o campo, suas regras de preenchimento e os controles de revisão.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-5">
          <FormSectionCard title="Identificação" icon={ClipboardList}>
           <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Nome do campo *</Label>
            <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Pneu dianteiro esquerdo" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Descrição</Label>
            <Textarea value={description} maxLength={1000} rows={2} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1">
              Tipo de Campo {typeLocked && <Lock className="h-3 w-3 text-muted-foreground" />}
            </Label>
            <Select value={fieldType} onValueChange={(v) => setFieldType(v as ChecklistFieldType)} disabled={typeLocked}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHECKLIST_FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {typeLocked && (
              <p className="text-xs text-muted-foreground">Item já publicado: para mudar o tipo, inative-o e crie outro.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Seção (opcional)</Label>
            <Select value={sectionId} onValueChange={setSectionId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SECTION}>Sem seção</SelectItem>
                {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
           </div>
          </FormSectionCard>

          <FormSectionCard title="Configuração do campo" icon={Settings2}>
           <div className="grid gap-4 sm:grid-cols-2">
          {!isFile && (
            <>
              <div className="space-y-1.5">
                <Label>Texto de Ajuda</Label>
                <Input value={placeholder} maxLength={200} onChange={(e) => setPlaceholder(e.target.value)} placeholder="Aparece dentro do campo quando vazio" />
              </div>
              <div className="space-y-1.5">
                <Label>Valor Padrão</Label>
                <Input value={defaultValue} maxLength={200} onChange={(e) => setDefaultValue(e.target.value)} placeholder="Pré-preenche o campo" />
              </div>
            </>
          )}

          {/* Opções por tipo */}
          {isSelect && (
            <div className="space-y-2 sm:col-span-2">
              <Label>Opções *</Label>
              <div className="flex gap-2">
                <Input value={newOption} maxLength={100} onChange={(e) => setNewOption(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }} placeholder="Nova opção" />
                <Button type="button" size="xs" variant="outline" onClick={addOption}><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {options.map((o) => (
                  <span key={o} className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs">
                    {o}
                    <button type="button" aria-label={`Remover ${o}`} onClick={() => setOptions(options.filter((x) => x !== o))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          {fieldType === 'number' && (
            <>
              <div className="space-y-1.5"><Label>Mínimo</Label><Input type="number" value={min} onChange={(e) => setMin(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Máximo</Label><Input type="number" value={max} onChange={(e) => setMax(e.target.value)} /></div>
            </>
          )}
          {fieldType === 'text' && (
            <div className="space-y-1.5"><Label>Limite de caracteres</Label><Input type="number" min={1} max={5000} value={maxLength} onChange={(e) => setMaxLength(e.target.value)} /></div>
          )}
          {isFile && (
            <div className="space-y-1.5"><Label>Quantidade máxima de arquivos</Label><Input type="number" min={1} max={20} value={maxFiles} onChange={(e) => setMaxFiles(e.target.value)} /></div>
          )}
          {supportsReference && (
            <div className="space-y-2 sm:col-span-2">
              <Label>Foto modelo (opcional)</Label>
              <input ref={referenceInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return setError('Use uma imagem JPEG, PNG ou WEBP');
                if (file.size > 5 * 1024 * 1024) return setError('A foto modelo deve ter até 5 MB');
                if (referencePreview?.startsWith('blob:')) URL.revokeObjectURL(referencePreview);
                setReferenceFile(file); setReferencePhoto(null); setReferencePreview(URL.createObjectURL(file)); setError(null);
              }} />
              {referencePreview ? (
                <div className="flex items-center gap-3 rounded-md border p-2">
                  <img src={referencePreview} alt="Prévia da foto modelo" className="h-20 w-28 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">{referenceFile?.name ?? referencePhoto?.file_name}</p>
                    <p className="text-xs text-muted-foreground">Aparecerá como orientação durante a vistoria.</p>
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Remover foto modelo" onClick={() => {
                    if (referencePreview.startsWith('blob:')) URL.revokeObjectURL(referencePreview);
                    setReferenceFile(null); setReferencePhoto(null); setReferencePreview(null);
                  }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ) : (
                <Button type="button" variant="outline" className="h-11 w-full border-dashed" onClick={() => referenceInputRef.current?.click()}>
                  <ImagePlus className="mr-2 h-4 w-4" /> Adicionar foto modelo
                </Button>
              )}
              {referencePreview && <Button type="button" size="xs" variant="outline" onClick={() => referenceInputRef.current?.click()}>Substituir imagem</Button>}
              <p className="text-xs text-muted-foreground">JPEG, PNG ou WEBP, até 5 MB. A imagem será congelada na próxima versão publicada.</p>
            </div>
          )}
           </div>
          </FormSectionCard>

          {/* Regras operacionais do item. */}
          <FormSectionCard title="Regras" icon={ShieldCheck}>
          <div className="space-y-3">
            <ToggleRow label="Obrigatório" checked={isRequired} onChange={setIsRequired} />
            <ToggleRow label="Exige aprovação na revisão" checked={requiresApproval} onChange={setRequiresApproval} />
            {fieldType === 'photo' && <ToggleRow label="Somente câmera (bloqueia galeria)" checked={cameraOnly} onChange={setCameraOnly} />}
            <ToggleRow label="Ativo" checked={isActive} onChange={setIsActive} />
          </div>
          </FormSectionCard>

        {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t bg-muted/10 px-6 py-3">
          <Button size="sm" className="h-7 px-2 sm:px-3" variant="outline" onClick={() => onOpenChange(false)}>
            <X className="mr-1 h-4 w-4" /> Cancelar
          </Button>
          <Button size="sm" className="h-7 px-2 sm:px-3" onClick={handleSave} disabled={saving || uploadingReference}>
            {uploadingReference ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} {saving || uploadingReference ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-4 text-sm">
      <span>{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
